import React, {
  useMemo,
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from 'react';
import { useSelector } from 'react-redux';
import { XNavigation } from '@core/utils';
import { Spinner } from '@core/components';
import { IItemProps } from 'devextreme-react/toolbar';
import { useTranslation } from '@core/hooks';
import SideNavBarLayout from './SideNavBarLayout/SideNavBarLayout';
import {
  Switch,
  Route,
  Redirect,
  HashRouter as Router
} from 'react-router-dom';
import { HOC, Module } from '../XAppWrapper/XAppWrapper';
import Breadcrumbs from './Breadcrumbs/Breadcrumbs';
import { prepareDynamicNavItems } from './utils/prepareDynamicNavItems';

export { default as HandledNavLink } from './SideNavBarLayout/Header/HandledNavLink/HandledNavLink';
export { prepareNavItems } from './utils/prepareNavItems';

export type HeaderItem = IItemProps | ((key: number) => JSX.Element);

export interface NavItemCustomProps {
  [key: string]: any;
}

export interface NavItem {
  text?: string;
  tooltip?: string;
  translationsPre?: string;
  roles?: number[];
  path?: string;
  component?: React.FC<any>;
  customProps?: NavItemCustomProps;
  items?: NavItem[];
  shouldNotTranslate?: boolean;
}

type HeaderItemWithId = {
  id: number;
  content: HeaderItem;
};

interface NavigationContextValue {
  headerItems: HeaderItemWithId[];
  navigationData: any;
  setNavigationData: any;
  navItems: NavItem[];
}

export type DynamicNavItemFetchFn = () => Promise<NavItem[]>;

export type DynamicNavItemsProvider = {
  moduleTranslationsPre: string | undefined;
  fetchFns: DynamicNavItemFetchFn[];
};

export interface CreateSideNavModuleArgs {
  navItems: NavItem[];
  homePath: string | undefined;
  headerItems: HeaderItem[];
  dynamicNavItemsProviders: DynamicNavItemsProvider[];
}

const NavigationContext = createContext<NavigationContextValue>(
  {} as NavigationContextValue
);
export const useNavigation = () => useContext(NavigationContext);

interface NavigationProviderProps {
  navItems: NavItem[];
  headerItems: HeaderItemWithId[];
}

const NavigationProvider: React.FC<NavigationProviderProps> = ({
  children,
  navItems,
  headerItems
}) => {
  const [navigationData, setNavigationData] = useState({});

  return (
    <NavigationContext.Provider
      value={{ navigationData, setNavigationData, navItems, headerItems }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const withNavigationWatcher: HOC = (Component) => (props) => {
  const { path } = props.match;
  const { setNavigationData } = useNavigation();

  useEffect(() => {
    setNavigationData({ currentPath: path });
  }, [path, setNavigationData]);

  return React.createElement(Component, props);
};

// temp wrapped for external logic
const withNavLayout = (
  WrappedComponent: React.FC<any>,
  {
    navItems,
    homePath,
    headerItemsWithIds,
    dynamicNavItemsProviders
  }: Omit<CreateSideNavModuleArgs, 'headerItems'> & {
    headerItemsWithIds: HeaderItemWithId[];
  }
) => () => {
  const [dynamicNavItemsLoaded, setDynamicNavItemsLoaded] = useState(
    !dynamicNavItemsProviders.length
  );

  const finalNavItems = useRef<NavItem[]>(navItems);

  const userRole = useSelector((state) => state.user.data.role);

  const { t } = useTranslation();

  const allowedRoutes = useMemo(() => {
    if (!dynamicNavItemsLoaded) return [];

    return XNavigation.selectAllowedRoutes(userRole, finalNavItems.current, t);
  }, [userRole, dynamicNavItemsLoaded]);

  useEffect(() => {
    if ((userRole & 2) > 0){
      prepareDynamicNavItems(dynamicNavItemsProviders).then((loadedNavItems) => {
        finalNavItems.current = [...finalNavItems.current, ...loadedNavItems];
        setDynamicNavItemsLoaded(true);
      })
    }
    else{
      setDynamicNavItemsLoaded(true);
    }

  }, []);

  if (!dynamicNavItemsLoaded) {
    return <Spinner />;
  }

  return (
    <>
      <Router>
        <NavigationProvider
          navItems={finalNavItems.current}
          headerItems={headerItemsWithIds}
        >
          <SideNavBarLayout>
            <Breadcrumbs navTree={allowedRoutes} />
            <Switch>
              {allowedRoutes.map(({ path, component }) => (
                <Route exact key={path} path={path} component={component} />
              ))}
              {homePath && <Redirect to={homePath} />}
            </Switch>
          </SideNavBarLayout>
        </NavigationProvider>
      </Router>
      <WrappedComponent />
      <a className="new-instance-button" href="/" target="_blank">
        <p>Otwórz nową instancję</p>
      </a>
    </>
  );
};

export const createSideNavModule = (
  sideNavSettings: CreateSideNavModuleArgs
): Module => {
  const headerItemsWithIds = sideNavSettings.headerItems.map<HeaderItemWithId>(
    (headerItem, i) => ({
      id: i,
      content: headerItem
    })
  );

  return {
    hoc: (WrappedComponent) =>
      withNavLayout(WrappedComponent, {
        ...sideNavSettings,
        headerItemsWithIds
      })
  };
};

export default withNavLayout;
